import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Logo } from "../Logo";
import { useLead } from "../../context/LeadContext";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Simulator", href: "/#simulator" },
  { label: "How it works", href: "/#how" },
  { label: "ROI Calculator", href: "/#roi" },
  { label: "Pricing", href: "/pricing" },
];

export const Navbar = () => {
  const { openLeadForm } = useLead();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -70 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4"
    >
      <nav
        className={`flex w-full max-w-6xl items-center justify-between rounded-2xl border px-4 py-2.5 transition-all duration-300 ${
          scrolled
            ? "border-silver-300/60 bg-white/80 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
        data-testid="navbar"
      >
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <Logo />
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {links.map((l) =>
            l.href.startsWith("/#") && pathname === "/" ? (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-${l.label}`}
                className="rounded-lg px-3.5 py-2 text-sm font-semibold text-silver-700 transition hover:bg-silver-100 hover:text-charcoal"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.href.startsWith("/#") ? "/" : l.href}
                data-testid={`nav-${l.label}`}
                className="rounded-lg px-3.5 py-2 text-sm font-semibold text-silver-700 transition hover:bg-silver-100 hover:text-charcoal"
              >
                {l.label}
              </Link>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="nav-cta-btn"
            onClick={() => openLeadForm({ source: "navbar" })}
            className="hidden bg-brand font-bold text-white hover:bg-brand-hover sm:inline-flex"
          >
            Get my demo
          </Button>
          <button
            className="md:hidden"
            onClick={() => setOpenMenu((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {openMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {openMenu && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-20 w-[92%] rounded-2xl border border-silver-300/60 bg-white p-3 shadow-xl md:hidden"
        >
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.href.startsWith("/#") ? "/" : l.href}
              onClick={() => setOpenMenu(false)}
              className="block rounded-lg px-4 py-3 text-sm font-semibold text-silver-700 hover:bg-silver-100"
            >
              {l.label}
            </Link>
          ))}
          <Button
            onClick={() => {
              setOpenMenu(false);
              openLeadForm({ source: "navbar_mobile" });
            }}
            className="mt-2 w-full bg-brand font-bold text-white hover:bg-brand-hover"
          >
            Get my demo
          </Button>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Navbar;
